import React, { useEffect, useState } from "react";
import { Plus, Trash2, PoundSterling, Calendar as CalIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import DateInput from "../../components/DateInput";

/* ---------- Tariff editor ---------- */
/* Each site's Design tab shows one section per tariff type (PPA / Export /
   Import) with an inline list of tariff bands. Editing is done in-place;
   adding a band opens a compact modal so the user can nail the date range
   before it becomes active. Backend endpoints:
     GET    /projects/{pid}/tariffs
     POST   /projects/{pid}/tariffs
     PATCH  /tariffs/{tid}
     DELETE /tariffs/{tid}                                                  */

const TARIFF_TYPES = [
  { key: "ppa",    label: "PPA rate",    hint: "Charged to the customer per self-consumed kWh.",              icon: "P" },
  { key: "export", label: "Export rate", hint: "Received per kWh exported to the grid (e.g. SEG contract).",   icon: "E" },
  { key: "import", label: "Import rate", hint: "Customer's grid alternative — reference for savings display.", icon: "I" },
];

export default function TariffsSection({ project }) {
  const projectId = project.id;
  const [tariffs, setTariffs] = useState([]);
  const [showModal, setShowModal] = useState(null);   // {type, editing?}
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/tariffs`);
      setTariffs(data.tariffs || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, [projectId]);

  const remove = async (tid) => {
    if (!window.confirm("Delete this tariff band? Historical P&L will fall back to the flat rate.")) return;
    setBusy(true);
    try {
      await api.delete(`/tariffs/${tid}`);
      toast.success("Tariff deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const groupedByType = TARIFF_TYPES.map((t) => ({
    ...t,
    bands: tariffs.filter((x) => x.tariff_type === t.key)
      .sort((a, b) => a.effective_from.localeCompare(b.effective_from)),
  }));

  return (
    <section className="swiss-card-flat p-6" data-testid="tariffs-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="label flex items-center gap-2">
            <PoundSterling size={11} /> Tariff schedule
          </div>
          <div className="muted text-xs mt-1">
            Time-varying rates per site. The P&amp;L calculator prices every 15-min sample
            against the tariff active on its timestamp.
          </div>
        </div>
      </div>

      <div className="space-y-5">
        {groupedByType.map((t) => (
          <div key={t.key} data-testid={`tariff-group-${t.key}`}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-display font-semibold text-sm flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 bg-[var(--ink)] text-white font-mono text-[10px]">{t.icon}</span>
                  {t.label}
                  <span className="text-[10px] font-mono muted">({t.bands.length} band{t.bands.length === 1 ? "" : "s"})</span>
                </div>
                <div className="text-[11px] muted mt-0.5">{t.hint}</div>
              </div>
              <button onClick={() => setShowModal({ type: t.key })}
                      className="btn-ghost text-xs"
                      data-testid={`add-tariff-${t.key}`}>
                <Plus size={12} /> Add band
              </button>
            </div>
            {t.bands.length === 0 ? (
              <div className="text-[11px] muted italic pl-7">
                No bands yet — P&amp;L falls back to the site&apos;s flat {t.key} rate.
              </div>
            ) : (
              <div className="border border-[var(--line)]">
                <table className="w-full text-xs data-table">
                  <thead>
                    <tr>
                      <th className="text-left">From</th>
                      <th className="text-left">To</th>
                      <th className="text-right">Rate (p/kWh)</th>
                      <th className="text-left">Note</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.bands.map((b) => (
                      <tr key={b.id} data-testid={`tariff-row-${b.id}`}>
                        <td className="font-mono"><CalIcon size={10} className="inline mr-1 muted" />{b.effective_from}</td>
                        <td className="font-mono muted">{b.effective_to || "— current —"}</td>
                        <td className="font-mono text-right font-bold text-[var(--accent)]">{b.rate_p_per_kwh.toFixed(2)}p</td>
                        <td className="text-[11px] muted">{b.note || "—"}</td>
                        <td>
                          <button onClick={() => remove(b.id)} disabled={busy}
                                  className="p-1 hover:bg-red-50 text-red-600"
                                  data-testid={`delete-tariff-${b.id}`} title="Delete">
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>

      {showModal && (
        <TariffModal
          projectId={projectId}
          initialType={showModal.type}
          onClose={() => setShowModal(null)}
          onSaved={() => { setShowModal(null); load(); }}
        />
      )}
    </section>
  );
}


function TariffModal({ projectId, initialType, onClose, onSaved }) {
  const [type, setType] = useState(initialType || "ppa");
  const [rate, setRate] = useState("");
  const [effFrom, setEffFrom] = useState("");
  const [effTo, setEffTo] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!rate || !effFrom) return toast.error("Rate and effective-from date are required");
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/tariffs`, {
        tariff_type: type,
        rate_p_per_kwh: Number(rate),
        effective_from: effFrom,
        effective_to: effTo || null,
        note: note || null,
      });
      toast.success("Tariff band added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/45 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="tariff-modal">
      <div className="bg-white w-full max-w-md border border-[var(--line)]"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[var(--line)]">
          <div className="label">Add tariff band</div>
          <div className="font-display text-xl font-semibold mt-1">New rate window</div>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="label mb-1.5">Tariff type</div>
            <div className="flex bg-[var(--surface-2)] border border-[var(--line)]">
              {TARIFF_TYPES.map((t) => (
                <button key={t.key} onClick={() => setType(t.key)}
                        data-testid={`tariff-modal-type-${t.key}`}
                        className={`flex-1 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest ${type === t.key ? "bg-[var(--ink)] text-white" : "muted"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="label mb-1.5">Effective from *</div>
              <DateInput className="input-swiss font-mono w-full"
                     value={effFrom} onChange={(e) => setEffFrom(e.target.value)}
                     data-testid="tariff-modal-from" />
            </div>
            <div>
              <div className="label mb-1.5">Effective to</div>
              <DateInput className="input-swiss font-mono w-full"
                     value={effTo} onChange={(e) => setEffTo(e.target.value)}
                     data-testid="tariff-modal-to" />
              <div className="text-[10px] muted mt-1">Leave blank = current</div>
            </div>
          </div>
          <div>
            <div className="label mb-1.5">Rate (p/kWh) *</div>
            <input type="number" step="0.01" className="input-swiss font-mono w-full"
                   value={rate} onChange={(e) => setRate(e.target.value)}
                   placeholder="18.50" data-testid="tariff-modal-rate" />
          </div>
          <div>
            <div className="label mb-1.5">Note</div>
            <input className="input-swiss w-full" value={note}
                   onChange={(e) => setNote(e.target.value)}
                   placeholder="Contract reference, supplier, etc."
                   data-testid="tariff-modal-note" />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-[var(--line)] bg-[var(--surface-2)]">
          <button onClick={onClose} className="btn-ghost" data-testid="tariff-modal-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-accent" data-testid="tariff-modal-save">
            {busy ? "Saving…" : "Add band"}
          </button>
        </div>
      </div>
    </div>
  );
}
